import React from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { css } from 'styled-components/macro'; //eslint-disable-line
import { SectionHeading, Subheading as SubheadingBase } from 'components/misc/Headings';
import { PrimaryButton as PrimaryButtonBase } from 'components/misc/Buttons';
import { ReactComponent as BriefcaseIcon } from 'feather-icons/dist/icons/briefcase.svg';
import { ReactComponent as MoneyIcon } from 'feather-icons/dist/icons/dollar-sign.svg';
import TeamIllustrationSrc from 'assets/images/team-illustration-2.svg';

const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const TextColumn = styled(Column)((props) => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`,
]);

const Image = styled.img((props) => [
  props.imageRounded && tw`rounded`,
  props.imageBorder && tw`border`,
  props.imageShadow && tw`shadow`,
]);

const TextContent = tw.div`lg:py-8 text-center md:text-left`;

const Subheading = tw(SubheadingBase)`text-center md:text-left`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`;

const Features = tw.div`mt-8 max-w-sm mx-auto md:mx-0`;
const Feature = tw.div`mt-8 flex items-start flex-col md:flex-row`;

const FeatureIconContainer = styled.div`
  ${tw`inline-block border border-primary-500 text-center rounded-full p-2 flex-shrink-0`}
  svg {
    ${tw`w-5 h-5 text-primary-500`}
  }
`;

const FeatureText = tw.div`mt-4 md:mt-0 md:ml-4 text-center md:text-left`;
const FeatureHeading = tw.div`font-bold text-lg text-primary-500`;
const FeatureDescription = tw.div`mt-1 text-sm`;

const PrimaryButton = styled(PrimaryButtonBase)((props) => [
  tw`mt-12 text-sm inline-block mx-auto md:mx-0`,
  props.buttonRounded && tw`rounded-full`,
]);

export default ({
  subheading = 'Our Expertise',
  heading = (
    <>
      We have the most <span tw='text-primary-500'>professional</span> marketing team.
    </>
  ),
  subheadingDown = null,
  description = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.',
  primaryButtonConfig = {
    as: 'a',
    primaryButtonText: 'See our Portfolio',
    primaryButtonUrl: 'https://timerse.com',
  },
  features = null,
  textOnLeft = true,
  imageSrc = TeamIllustrationSrc,
  buttonRounded = true,
  imageRounded = true,
  imageBorder = false,
  imageShadow = false,
}) => {
  // The textOnLeft boolean prop can be used to display either the text on left or right side of the image.

  /*
   * Change the features variable as you like, add or delete objects
   * `icon` must be a React SVG component. See how BriefcaseIcon is imported above. For a full list of available icons, see Feather Icons.
   */
  const defaultFeatures = [
    {
      Icon: BriefcaseIcon,
      title: 'Professionalism',
      description: 'We have the best professional marketing people across the globe just to work with you.',
    },
    {
      Icon: MoneyIcon,
      title: 'Affordable',
      description: 'We promise to offer you the best rate we can - at par with the industry standard.',
    },
  ];

  if (!features) features = defaultFeatures;

  const hasFeatures = features.length > 0;

  return (
    <Container>
      <TwoColumn>
        <ImageColumn>
          <Image src={imageSrc} imageBorder={imageBorder} imageShadow={imageShadow} imageRounded={imageRounded} />
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            {heading && <Heading>{heading}</Heading>}
            {subheadingDown && <Subheading>{subheadingDown}</Subheading>}
            {description && <Description>{description}</Description>}
            <Features>
              {hasFeatures &&
                features.map((feature, index) => {
                  return (
                    <Feature key={index}>
                      <FeatureIconContainer>{<feature.Icon />}</FeatureIconContainer>
                      <FeatureText>
                        <FeatureHeading>{feature.title}</FeatureHeading>
                        {feature.description && <FeatureDescriptionMulti description={feature.description} />}
                      </FeatureText>
                    </Feature>
                  );
                })}
            </Features>
            <PrimaryButton
              buttonRounded={buttonRounded}
              as={primaryButtonConfig.as}
              href={primaryButtonConfig.primaryButtonUrl || null}
              onClick={primaryButtonConfig.onClick}
            >
              {primaryButtonConfig.primaryButtonText}
            </PrimaryButton>
          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};

const FeatureDescriptionMulti = ({ description }) => {
  const hasMultiLineDescription = description && description.split('\n').length > 0;

  return (
    <>
      {hasMultiLineDescription ? (
        description
          .split('\n')
          .map((line, index) => <FeatureDescription key={`description-paragraph-${index}`}>{line}</FeatureDescription>)
      ) : (
        <FeatureDescription>{description}</FeatureDescription>
      )}
    </>
  );
};
